import axios from 'axios';
import { API_URL } from './api';

// -------------------- helpers --------------------
const _err = (error) => {
    if (error.response) {
        console.error('Partner API error:', error.response.status, error.response.data);
    } else {
        console.error('Partner API error:', error.message);
    }
};

// Отдельный helper для запросов от имени ПАРТНЁРА — без X-Platform.
const partnerAuth = (token) => ({
    headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
    },
});


// ===================== СТАТИСТИКА =====================

// GET /partner/stats/?period=today|7d|30d|90d|all
export const APIGetPartnerStats = async (token, period = '30d') => {
    try {
        const response = await axios.get(
            `${API_URL}/partner/stats/?period=${period}`,
            partnerAuth(token)
        );
        return response.data;
    } catch (e) { _err(e); throw e; }
};


// ===================== ПРОФИЛЬ И РЕКВИЗИТЫ =====================

// GET /partner/profile/
export const APIGetPartnerProfile = async (token) => {
    try {
        const response = await axios.get(
            `${API_URL}/partner/profile/`,
            partnerAuth(token)
        );
        return response.data;
    } catch (e) { _err(e); throw e; }
};

// PATCH /partner/profile/update/   {full_name, phone}
// ВАЖНО: email теперь меняется отдельным флоу — здесь email не отправляем.
export const APIUpdatePartnerProfile = async (token, data) => {
    try {
        const response = await axios.patch(
            `${API_URL}/partner/profile/update/`,
            data,
            partnerAuth(token)
        );
        return response.data;
    } catch (e) { _err(e); throw e; }
};

// PATCH /partner/bank-details/   {card_holder_name, card_number, bank_name, inn}
export const APIUpdatePartnerBankDetails = async (token, data) => {
    try {
        const response = await axios.patch(
            `${API_URL}/partner/bank-details/`,
            data,
            partnerAuth(token)
        );
        return response.data;
    } catch (e) { _err(e); throw e; }
};


// ===================== БАЛАНС / НАЧИСЛЕНИЯ / ВЫПЛАТЫ =====================

export const APIGetPartnerBalance = async (token) => {
    try {
        const response = await axios.get(`${API_URL}/partner/balance/`, partnerAuth(token));
        return response.data;
    } catch (e) { _err(e); throw e; }
};

export const APIGetPartnerEarnings = async (token, { status, page = 1, pageSize = 20 } = {}) => {
    try {
        const params = new URLSearchParams();
        if (status) params.append('status', status);
        params.append('page', page);
        params.append('page_size', pageSize);
        const response = await axios.get(
            `${API_URL}/partner/earnings/?${params.toString()}`,
            partnerAuth(token)
        );
        return response.data;
    } catch (e) { _err(e); throw e; }
};

export const APIGetPartnerPayouts = async (token, status = null) => {
    try {
        const url = status
            ? `${API_URL}/partner/payouts/?status=${status}`
            : `${API_URL}/partner/payouts/`;
        const response = await axios.get(url, partnerAuth(token));
        return response.data;
    } catch (e) { _err(e); throw e; }
};

export const APIGetPartnerPayoutDetail = async (token, payoutId) => {
    try {
        const response = await axios.get(
            `${API_URL}/partner/payouts/${payoutId}/`,
            partnerAuth(token)
        );
        return response.data;
    } catch (e) { _err(e); throw e; }
};

export const APIRequestPartnerPayout = async (token, { amount, method = 'card' }) => {
    try {
        const response = await axios.post(
            `${API_URL}/partner/payouts/request/`,
            { amount, method },
            partnerAuth(token)
        );
        return response.data;
    } catch (e) { _err(e); throw e; }
};


// ===================== ПОДДЕРЖКА =====================

export const APIGetSupportTickets = async (token) => {
    try {
        const response = await axios.get(`${API_URL}/partner/support/tickets/`, partnerAuth(token));
        return response.data;
    } catch (e) { _err(e); throw e; }
};

export const APICreateSupportTicket = async (token, { subject, category = 'other', text }) => {
    try {
        const response = await axios.post(
            `${API_URL}/partner/support/tickets/create/`,
            { subject, category, text },
            partnerAuth(token)
        );
        return response.data;
    } catch (e) { _err(e); throw e; }
};

export const APIGetSupportTicket = async (token, ticketId) => {
    try {
        const response = await axios.get(
            `${API_URL}/partner/support/tickets/${ticketId}/`,
            partnerAuth(token)
        );
        return response.data;
    } catch (e) { _err(e); throw e; }
};

export const APIReplySupportTicket = async (token, ticketId, text) => {
    try {
        const response = await axios.post(
            `${API_URL}/partner/support/tickets/${ticketId}/reply/`,
            { text },
            partnerAuth(token)
        );
        return response.data;
    } catch (e) { _err(e); throw e; }
};

export const APICloseSupportTicket = async (token, ticketId) => {
    try {
        const response = await axios.post(
            `${API_URL}/partner/support/tickets/${ticketId}/close/`,
            {},
            partnerAuth(token)
        );
        return response.data;
    } catch (e) { _err(e); throw e; }
};


// ===================== СОВЕТЫ И РЕКОМЕНДАЦИИ =====================

export const APIGetPartnerTips = async (token, category = null) => {
    try {
        const url = category
            ? `${API_URL}/partner/tips/?category=${category}`
            : `${API_URL}/partner/tips/`;
        const response = await axios.get(url, partnerAuth(token));
        return response.data;
    } catch (e) { _err(e); throw e; }
};

export const APIGetPartnerRecommendations = async (token) => {
    try {
        const response = await axios.get(
            `${API_URL}/partner/recommendations/`,
            partnerAuth(token)
        );
        return response.data;
    } catch (e) { _err(e); throw e; }
};

export const APIDismissPartnerRecommendation = async (token, recId) => {
    try {
        const response = await axios.post(
            `${API_URL}/partner/recommendations/${recId}/dismiss/`,
            {},
            partnerAuth(token)
        );
        return response.data;
    } catch (e) { _err(e); throw e; }
};


// ===================== БОНУСНАЯ ПРОГРАММА =====================

export const APIGetBonusRules = async (token) => {
    try {
        const response = await axios.get(`${API_URL}/partner/bonus/rules/`, partnerAuth(token));
        return response.data;
    } catch (e) { _err(e); throw e; }
};

export const APIGetBonusProgress = async (token) => {
    try {
        const response = await axios.get(`${API_URL}/partner/bonus/progress/`, partnerAuth(token));
        return response.data;
    } catch (e) { _err(e); throw e; }
};

export const APIGetBonusHistory = async (token, { page = 1, pageSize = 20 } = {}) => {
    try {
        const response = await axios.get(
            `${API_URL}/partner/bonus/history/?page=${page}&page_size=${pageSize}`,
            partnerAuth(token)
        );
        return response.data;
    } catch (e) { _err(e); throw e; }
};

// ===================== РЕКЛАМА НА ТЕРРИТОРИИ =====================

const _range = (params = {}) => {
    const s = new URLSearchParams();
    if (params.dateFrom) s.append('date_from', params.dateFrom);
    if (params.dateTo) s.append('date_to', params.dateTo);
    if (params.onlyActive) s.append('only_active', 'true');
    if (params.page) s.append('page', params.page);
    if (params.pageSize) s.append('page_size', params.pageSize);
    return s.toString();
};

export const APIPartnerTerritorySummary = async (token, opts = {}) => {
    try {
        const r = await axios.get(`${API_URL}/partner/territory/summary/?${_range(opts)}`, partnerAuth(token));
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIPartnerTerritoryTimeline = async (token, opts = {}) => {
    try {
        const r = await axios.get(`${API_URL}/partner/territory/timeline/?${_range(opts)}`, partnerAuth(token));
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIPartnerTerritoryCities = async (token, opts = {}) => {
    try {
        const r = await axios.get(`${API_URL}/partner/territory/cities/?${_range(opts)}`, partnerAuth(token));
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIPartnerTerritoryCapacity = async (token) => {
    try {
        const r = await axios.get(`${API_URL}/partner/territory/capacity/`, partnerAuth(token));
        return r.data; // { success, territory, capacity, slots, peak_hours, status }
    } catch (e) { _err(e); throw e; }
};

export const APIPartnerTerritoryAds = async (token, opts = {}) => {
    try {
        const r = await axios.get(`${API_URL}/partner/territory/ads/?${_range(opts)}`, partnerAuth(token));
        return r.data; // { success, items, total, page, pages }
    } catch (e) { _err(e); throw e; }
};

export const APIPartnerTerritoryAdDetail = async (token, campaignId, opts = {}) => {
    try {
        const r = await axios.get(`${API_URL}/partner/territory/ads/${campaignId}/?${_range(opts)}`, partnerAuth(token));
        return r.data;
    } catch (e) { _err(e); throw e; }
};

// ===================== ДОХОД С РЕКЛАМЫ =====================

export const APIPartnerAdRevenueSummary = async (token, opts = {}) => {
    try {
        const r = await axios.get(`${API_URL}/partner/ad-revenue/summary/?${_range(opts)}`, partnerAuth(token));
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIPartnerAdRevenueTimeline = async (token, opts = {}) => {
    try {
        const r = await axios.get(`${API_URL}/partner/ad-revenue/timeline/?${_range(opts)}`, partnerAuth(token));
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIPartnerAdRevenueBreakdown = async (token, opts = {}) => {
    try {
        const r = await axios.get(`${API_URL}/partner/ad-revenue/breakdown/?${_range(opts)}`, partnerAuth(token));
        return r.data;
    } catch (e) { _err(e); throw e; }
};

// ===================== УВЕДОМЛЕНИЯ КАБИНЕТА =====================

export const APIPartnerNotifications = async (token, { onlyUnread = false, page = 1, pageSize = 20 } = {}) => {
    try {
        const s = new URLSearchParams();
        if (onlyUnread) s.append('only_unread', 'true');
        s.append('page', page); s.append('page_size', pageSize);
        const r = await axios.get(`${API_URL}/partner/notifications/?${s.toString()}`, partnerAuth(token));
        return r.data; // { success, unread, results, count, page, total_pages }
    } catch (e) { _err(e); throw e; }
};

export const APIPartnerNotificationsRead = async (token, { ids = null, all = false } = {}) => {
    try {
        const body = all ? { all: true } : { ids: ids || [] };
        const r = await axios.post(`${API_URL}/partner/notifications/read/`, body, partnerAuth(token));
        return r.data; // { success, updated }
    } catch (e) { _err(e); throw e; }
};


export const APIPartnerReferral = async (token) => {
    try {
        const response = await axios.get(`${API_URL}/partner/referral/me/`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};